/** English */
const en = {
  common: {
    save: "Save",
    saving: "Saving…",
    saved: "Saved",
    cancel: "Cancel",
    close: "Close",
    back: "Back",
    backToDashboard: "Back to Dashboard",
    loading: "Loading…",
    error: "Error",
    all: "All",
    yes: "Yes",
    no: "No",
    user: "User",
    admin: "Admin",
    enterpriseTier: "Enterprise Tier",
    globalReputation: "ReviewLytics",
    brandSubtitle: "Review Analytics",
    preferencesSaved: "Preferences updated successfully",
    unsavedChanges: "Unsaved changes detected. Remember to commit your updates.",
    allSaved: "All settings are saved and up to date.",
    mainNav: "Main navigation",
    documentation: "Documentation",
    support: "Support",
    settings: "Settings",
    search: "Search",
    open: "Open",
    active: "Active",
    enabled: "Enabled",
    disabled: "Disabled",
    systemStatusOptimal: "System Status: Optimal",
    storageUsage: "Storage usage",
    yourWorkspace: "Your Workspace",
  },
  nav: {
    dashboard: "Dashboard",
    billing: "Plans & Billing",
    reviewHub: "Review Hub",
    deletionCases: "Deletion Cases",
    emailAlerts: "Email Alerts",
    platforms: "Platforms",
    payment: "Payment",
    settings: "Settings",
    support: "Support",
    documentation: "Documentation",
    users: "Users",
    reviews: "Reviews",
    newCase: "New Case",
    adminDeletionCases: "Deletion Cases",
    premiumPerks: "Premium Benefits",
  },
  perks: {
    eyebrow: "Premium benefits",
    title: "Exclusive discounts",
    subtitle:
      "Your Professional membership unlocks preferred rates on digital services from our partner studio.",
    heroTitleNamed: "You're a Professional member, {{name}}",
    heroTitle: "You're a Professional member",
    heroDesc:
      "As a premium subscriber you receive 20% off every service below — from your business website to a fully custom CRM.",
    heroBadge: "Professional",
    discountBadge: "−20%",
    websiteDevTitle: "Website Development",
    websiteDevDesc:
      "A fast, modern business website — designed, built, and launched for your brand.",
    crmDevTitle: "CRM Development",
    crmDevDesc:
      "A custom CRM tailored to your workflows: leads, customers, pipelines, and reporting.",
    automationTitle: "Workflow Automation",
    automationDesc:
      "Automate repetitive busywork — integrations, syncs, and notifications that run themselves.",
    designTitle: "UI/UX Design",
    designDesc:
      "Premium interfaces and brand-consistent design systems for your digital products.",
    claimCta: "Request offer",
    note: "To redeem a discount, contact our team via Support and mention your Professional membership — the 20% is applied to your quote.",
    lockedTitle: "Exclusive discounts for Professional members",
    lockedDesc:
      "Upgrade to Professional to unlock 20% off website development, CRM development, and more.",
    lockedCta: "View plans",
  },
  jamedaConnect: {
    title: "Connect Jameda",
    subtitle:
      "Link your Jameda profile with your personal API key — your patient reviews sync automatically.",
    guideTitle: "Where do I find my credentials?",
    step1Title: "Sign in to Jameda",
    step1Detail:
      "Log in at jameda.de with your practice account and open your profile management area.",
    step2Title: "Open the API settings",
    step2Detail:
      "In the management menu go to “Einstellungen” → “Schnittstellen & API” (Settings → Integrations & API).",
    step3Title: "Generate your API key",
    step3Detail:
      "Click “Neuen API-Schlüssel generieren”, then copy the key. Treat it like a password — do not share it.",
    step4Title: "Copy your Business ID",
    step4Detail:
      "You'll find it right next to the API key — or in your profile URL, e.g. jameda.de/profil/12345678 → your ID is 12345678.",
    helpNote: "Can't find the API section in your Jameda account?",
    helpLink: "Our support team is happy to help",
    businessIdLabel: "Jameda Business ID",
    businessIdPlaceholder: "e.g. 12345678",
    businessIdHint: "The number from your Jameda profile URL or API settings.",
    apiKeyLabel: "Jameda API key",
    apiKeyPlaceholder: "Paste your API key",
    apiKeyHint: "Generated in your Jameda account under Integrations & API.",
    showKey: "Show API key",
    hideKey: "Hide API key",
    connectCta: "Verify & connect",
    connecting: "Verifying credentials…",
    errorRequired: "Please enter both your Business ID and your API key.",
    errorGeneric:
      "Connection failed. Please check your Business ID and API key and try again.",
    errorInvalidCredentials:
      "Jameda did not accept these credentials. Please double-check your Business ID and API key — step 3 and 4 in the guide show where to find them.",
    errorNotFound:
      "No Jameda profile was found for this Business ID. Please check the number (step 4 in the guide).",
    errorUnreachable:
      "Jameda is currently not reachable. Please try again in a few minutes.",
    securityNote:
      "Your API key is stored securely and only used to sync your reviews. You can disconnect at any time.",
  },
  sidebar: {
    loggingOut: "Signing out…",
    logout: "Log out",
    backToAdmin: "Back to Admin",
  },
  auth: {
    login: "Log in",
    loginTitle: "Welcome back",
    loginSubtitle: "Sign in to access your reputation dashboard.",
    email: "Email",
    password: "Password",
    rememberMe: "Remember me",
    forgotPassword: "Forgot password?",
    noAccount: "Don't have an account?",
    signUp: "Sign up",
    signingIn: "Signing in…",
    emailRequired: "Please enter your email address.",
    emailInvalid: "Please enter a valid email address.",
    passwordRequired: "Please enter your password.",
    loginFailed: "Login failed. Please check your credentials.",
    twoFactorTitle: "Check your email",
    twoFactorSubtitle: "For your security, we've emailed a 6-digit code to {{email}}. Enter it below to finish signing in.",
    twoFactorCodeLabel: "Verification code",
    twoFactorVerify: "Verify & sign in",
    twoFactorVerifying: "Verifying…",
    twoFactorResend: "Resend code",
    twoFactorResendIn: "Resend in {{seconds}}s",
    twoFactorBack: "Back to login",
    twoFactorExpired: "The login session expired. Please sign in again.",
    twoFactorInvalidCode: "Invalid or expired code. Please try again.",
    twoFactorDevMode: "Development mode: no email was sent. Find the 6-digit code in the server console.",
    signup: "Sign up",
    forgotPasswordTitle: "Forgot password",
    forgotPasswordSubtitle: "We'll send you a link to reset your password.",
    sendResetLink: "Send reset link",
    backToLogin: "Back to login",
    notFoundTitle: "Page not found",
    notFoundBody: "The page you requested does not exist.",
    goHome: "Go to home",
    consentPrefix: "By using this service you agree to our",
    resetLinkSending: "Sending…",
    resetLinkSent: "Send reset link",
    emailSentTitle: "Email sent",
    emailSentBody:
      "If an account exists with this email address, we sent you a password reset link.",
    resetTitle: "Set a new password",
    resetSubtitle: "Choose a new password for your account.",
    resetNewPassword: "New password",
    resetConfirmPassword: "Confirm password",
    resetSubmit: "Save new password",
    resetSaving: "Saving…",
    resetMismatch: "The passwords do not match.",
    resetTooShort: "The password must be at least 8 characters.",
    resetSuccessTitle: "Password changed",
    resetSuccessBody: "Your password has been changed. You can now sign in with it.",
    resetInvalidToken:
      "This reset link is invalid or has expired. Please request a new one.",
    resetGenericError: "Something went wrong. Please try again.",
    goToLogin: "Go to login",
    signupStepPersonal: "Personal details",
    signupStepCompany: "Company details",
    fullName: "Full name",
    companyName: "Company name",
    industry: "Industry / CEO",
    businessEmail: "Business email",
    businessAddress: "Business address",
    next: "Next",
    back: "Back",
    createAccount: "Create account",
    haveAccount: "Already have an account?",
    registerFailed: "Registration failed. Please try again.",
    registerCheckDetails: "Registration failed. Please check your details.",
    fullNameRequired: "Please enter your full name.",
    companyNameRequired: "Please enter your company name.",
    businessEmailRequired: "Please enter your business email.",
    businessAddressRequired: "Please enter your business address.",
    industryRequired: "Please enter your industry.",
    passwordMinLength: "Password must be at least 8 characters.",
  },
  settings: {
    title: "Settings",
    subtitle:
      "Configure your enterprise workspace preferences, notification triggers, and security protocols.",
    general: "General Settings",
    language: "Language selection",
    languageEn: "English (United States)",
    languageDe: "German (DE)",
    timezone: "Timezone",
    reviewsInbox: "Reviews & Inbox",
    autoSync: "Auto-sync reviews on login",
    autoSyncHint:
      "Automatically fetch latest audits and review data upon session start.",
    emailNotifications: "Email notifications",
    emailNotificationsHint:
      "Receive daily summaries and critical alerts via registered email.",
    showResolved: "Show resolved reviews in inbox",
    showResolvedHint:
      "Keep archived audit histories visible in your primary workspace view.",
    accountSecurity: "Account & Security",
    password: "Password",
    passwordLastChanged: "Last changed 4 months ago",
    changePassword: "Change Password",
    twoFactor: "Two-Factor Auth",
    twoFactorHint: "Enhanced security via email code at login",
    twoFactorEnable: "Enable",
    twoFactorDisable: "Turn off",
    twoFactorSending: "Sending code…",
    twoFactorEnterCode: "Enter the 6-digit code we just sent to {{email}}.",
    twoFactorConfirm: "Confirm",
    twoFactorCodeInvalid: "Invalid or expired code. Please try again.",
    twoFactorPasswordPrompt: "Enter your account password to turn off two-factor authentication.",
    twoFactorPasswordInvalid: "Incorrect password.",
    twoFactorError: "Something went wrong. Please try again.",
    twoFactorDevMode: "Development mode: no email was sent — the code was printed to the server console.",
    twoFactorEmailFailed: "The verification code could not be emailed. Please check the email (SMTP) configuration and try again.",
    twoFactorEmailNotConfigured: "Email delivery is not set up yet. Please configure SMTP credentials before enabling two-factor authentication.",
    enterpriseNoticeTitle: "Enterprise Security Notice",
    enterpriseNoticeBody:
      "All alert data is encrypted end-to-end. Team notification settings are logged for audit purposes.",
    downloadAuditLog: "Download Audit Log",
    teamAccessPolicy: "Team Access Policy",
    saveSettings: "Save settings",
    saveError: "Could not save your settings. Please try again.",
    restartOnboarding: "Restart onboarding",
    supportResources: "Support Resources",
    apiDocs: "API Documentation",
    helpCenter: "Help Center",
    trustSecurity: "Trust & Security",
  },
  emailAlerts: {
    title: "Email Alerts",
    subtitle:
      "Configure when your team receives reputation notifications.",
    negativeReview: "Negative review alert",
    negativeReviewHint: "Instant email when a 1–3 star review is posted.",
    unreplied: "Unreplied review reminder",
    unrepliedHint: "Daily reminder for reviews without a response after 24 hours.",
    deletionUpdates: "Deletion case updates",
    deletionUpdatesHint:
      "Notify when case status changes (submitted, in progress, resolved).",
    ratingDrop: "Rating drop alert",
    ratingDropHint: "Alert when your average rating decreases significantly.",
    weeklyDigest: "Weekly reputation digest",
    weeklyDigestHint:
      "Summary of reviews, replies, and case activity every Monday.",
    savePreferences: "Save alert preferences",
    negativeReviewHintDynamic:
      "Instant email when a review with {{threshold}} stars or below is posted.",
    thresholdLabel: "Alert threshold",
    thresholdOption: "{{count}}★ or below",
    allActivity: "All review activity",
    allActivityHint: "Email for every new or updated review, regardless of rating.",
    ticketReply: "Support replies",
    ticketReplyHint: "Email when our team replies to one of your support tickets.",
    recipientNote:
      "Alerts are sent to your registered company email: {{email}}",
    loadError: "Could not load your alert settings. Please try again.",
    saveError: "Could not save your alert settings. Please try again.",
  },
  adminEmailAlertsReal: {
    newCase: "New deletion case",
    newCaseHint: "Email when a client submits a new deletion case.",
    newTicket: "New support ticket",
    newTicketHint: "Email when a client opens a new support ticket.",
    deliveryTitle: "Delivery & test",
    deliveryTransport: "Current transport: {{transport}}",
    deliveryRecipient: "Test emails go to your notification address: {{email}}",
    deliveryDevHint:
      "No SMTP credentials are set — emails print to the Django console. Add EMAIL_HOST, EMAIL_HOST_USER and EMAIL_HOST_PASSWORD in Backend/.env for live delivery.",
    previewButton: "Preview email",
    previewTitle: "Email preview",
    testButton: "Send test email",
    sendingTest: "Sending…",
    testSent: "Test email sent to {{email}}",
    testError: "Could not send the test email. Check server logs and email settings.",
  },
  home: {
    overview: "Reputation Overview",
    welcome: "Welcome to your reputation workspace",
    welcomeHint:
      "Monitor reviews, handle critical cases, and protect your brand.",
    accountStatus: "Account status",
    quickActions: "Quick actions",
    needsReply: "Needs Reply",
    needsReplyDesc: "Respond to reviews waiting for your answer.",
    criticalReviews: "Critical Reviews",
    criticalReviewsDesc: "1–3 star reviews impacting your rating.",
    deletionCases: "Deletion Cases",
    deletionCasesDesc: "Track legal removal requests and case progress.",
    connectPlatform: "Connect Platform",
    connectPlatformDesc: "Link Google Business or additional review sources.",
    activeCases: "Active cases",
    connectProfile: "Connect your profile",
    connectProfileHint:
      "Connect Google Business to see reviews and metrics.",
    goToPlatforms: "Go to Platforms",
    dashboardTitle: "Dashboard",
    greeting: "Welcome back, {{name}}",
    overviewSubtitle:
      "Monitor network health, respond to reviews, and manage deletion cases from one command center.",
    planLabel: "Plan",
    billingLink: "Plans & Billing",
    activeCasesLabel: "Active cases",
    activeCasesHint: "Deletion requests in progress",
    sentimentSnapshot: "Sentiment Snapshot",
    positive: "Positive",
    neutral: "Neutral",
    negative: "Negative",
    connectForSentiment: "Connect a profile to view sentiment distribution.",
    recommendedWorkflow: "Recommended Workflow",
    workflow1: "Open Review Hub → Critical tab for urgent reviews",
    workflow2: "Reply or request removal from the same review card",
    workflow3: "Track removal progress in Deletion Cases",
    defamationGuide: "Defamation Guide",
    defamationGuideDesc:
      "Learn when a review qualifies for legal removal and how to document your case.",
    readDocumentation: "Read documentation",
  },
  reviewHub: {
    needsReply: "Needs Reply",
    critical: "Critical",
    allReviews: "All Reviews",
    reply: "Reply",
    cancel: "Cancel",
    requestRemoval: "Request Removal",
    removeFromCart: "Remove from cart",
    sendReply: "Send reply",
    noReviews: "No reviews in this view.",
    loading: "Loading reviews…",
  },
  cases: {
    title: "Deletion Cases",
    subtitle: "Manage and track legal removal requests.",
    allStatus: "All Status",
    allViolations: "All violation types",
    searchPlaceholder: "Case ID, reason, or violation type…",
    filterStatus: "Filter by status",
    filterViolation: "Filter by violation type",
    status: {
      initiated: "Submitted",
      started: "In Progress",
      legal_action: "Legal Action",
      completed: "Completed",
      rejected: "Rejected",
      pending: "Pending",
      inProgress: "In Progress",
    },
    violation: {
      spam: "Spam / Fake review",
      conflict: "Conflict of interest",
      insult: "Insult / Hate speech",
      falseFacts: "False statement of fact",
      privacy: "Privacy violation",
      other: "Other",
    },
    createModal: {
      title: "Create deletion case",
      subtitle:
        "{{count}} review(s) will be added to a case. Our team will initiate the removal and notify you as soon as there is a result.",
      violationType: "Violation type",
      justification: "Justification",
      justificationPlaceholder:
        "Describe why these reviews should be removed…",
      justificationRequired: "Please enter a justification.",
      optional: "optional",
      selectReview: "Please select at least one review.",
      estimatedTotal: "Estimated total",
      perReview: "per review",
      reviews: "reviews",
      submit: "Submit case",
      submitting: "Submitting…",
      manualInvoicingNote:
        "The stated deletion costs are for orientation only. Only successfully removed reviews are charged. You will receive the invoice separately by email once the deletions are complete.",
      freeIncluded: "{{count}} review deletion(s) included free in this case",
      freeIncludedDetail:
        "Your Professional plan includes 2 free deletions every 30 days. These are applied automatically to the first reviews in this submission.",
      includedFree: "Included",
      freeLine: "{{count}} review(s) — included free",
      paidLine: "{{count}} review(s) × {{price}} (estimated)",
      anonymous: "Anonymous",
      noText: "No text",
    },
    activeConnections: "Active connections",
    activeConnection: "Active connection",
    loadingCases: "Loading deletion cases…",
    noCasesFilter: "No cases match these filters",
    noCasesYet: "No deletion cases yet",
    noCasesFilterHint: "Try adjusting status, violation type, or search.",
    noCasesYetHint:
      "Select one or more reviews and submit a deletion request to get started.",
    goToReviews: "Go to Reviews",
    newCase: "New case",
    violationLabel: "Violation type",
    justificationLabel: "Justification",
    downloadReport: "Report",
    showDetails: "Details",
    hideDetails: "Hide",
    statusResolved: "Resolved",
    footerCases: "{{count}} cases",
    privacyPolicy: "Privacy Policy",
    termsOfService: "Terms of Service",
    legalNotice: "Legal Notice",
    pageSubtitle:
      "Overview of your submitted deletion requests. Each case shows the associated ratings — expand a case for details.",
    createNewCase: "Create new case",
    downloadPdf: "Download PDF",
    viewDetails: "View details",
    requestedRatings: "Requested Ratings ({{count}})",
    submissionReason: "Submission reason",
    copyright: "© 2026 ReviewLytics. All rights reserved.",
    compliance: "Compliance",
    withdrawCase: "Withdraw case",
    deleteModal: {
      title: "Withdraw deletion case?",
      subtitle:
        "This permanently removes the case from your account. The linked reviews will become available for a new removal request.",
      reviewCount: "{{count}} review(s) in this case",
      hint: "You can only withdraw cases that have not started processing yet.",
      cancel: "Keep case",
      confirm: "Withdraw case",
      deleting: "Withdrawing…",
      success: "Case {{caseId}} was withdrawn successfully.",
    },
  },
  platforms: {
    emptyTitle: "Your reputation ecosystem is empty",
    emptyBody:
      "Connect your first platform to monitor reviews, analyze sentiment, and manage your brand footprint.",
    connectPlatform: "Connect platform",
    ecosystemTitle: "Platform Ecosystem",
    ecosystemSubtitle:
      "Manage your digital presence: connect Google Business and Jameda and unify your brand intelligence.",
    connectedPlatforms: "Connected platforms",
    addPlatform: "Add platform",
    syncing: "Syncing Google Business…",
    oauthTitle: "Linking Google…",
    oauthDetail: "The server is confirming your Google sign-in.",
    accountsTitle: "Loading Google accounts…",
    accountsDetail: "Processing server response.",
    locationsTitle: "Loading locations…",
    locationsDetail: "Account {{index}} of {{total}} — server response.",
    disconnecting: "Disconnecting…",
    disconnectingDetail: "Removing the platform from your account.",
    architectIntelligence: "ARCHITECT INTELLIGENCE PLATFORM",
    activeConnection: "ACTIVE CONNECTION",
    activeConnections: "ACTIVE CONNECTIONS",
    manage: "Manage",
    disconnect: "Disconnect",
    addPlatformHint:
      "Only sources you have not connected yet. Add more Google locations via the menu (⋮) on an existing connection.",
    connectAria: "Connect {{name}}",
    ecosystemStatus: "Ecosystem status",
    healthPartial:
      "Connect both channels for a complete view of your reputation.",
    healthOptimal:
      "Your platform sync is nearly optimal — all configured sources are active.",
    upcomingSyncs: "Upcoming syncs",
    metadataReconcile: "metadata reconcile",
    syncCadence: "every 6 hrs",
    locationOrdinal: "Location {{index}} of {{total}}",
    profileOrdinal: "Profile {{index}} of {{total}}",
    active: "Active",
    lastSynced: "Last synced {{time}}",
    apiOk: "API connection healthy",
    addAnotherGoogle: "Connect another location",
    addAnotherJameda: "Update Jameda credentials",
    addAnotherKununu: "Connect another Kununu profile",
    waitServer: "Please wait for the server response.",
    relativeJustNow: "just now",
    relativeMinutes: "{{count}} min ago",
    relativeHours: "{{count}} hr ago",
    relativeDays: "{{count}} day ago",
    relativeDaysPlural: "{{count}} days ago",
    googleDesc:
      "Connect your Google Business profile to sync reviews and local search performance into your dashboard.",
    googleBadge: "READY TO SYNC",
    jamedaDesc:
      "Connect your Jameda profile with an API key and sync patient reviews directly into your dashboard.",
    jamedaBadge: "PATIENT REVIEWS",
    kununuDesc:
      "Monitor employee sentiment and brand reputation on Europe's leading employer platform.",
    kununuBadge: "EMPLOYER BRANDING",
    errorNoAccounts:
      "Google is connected but no business accounts or locations were found.",
    errorLoadFailed: "Could not load Google Business.",
    errorOAuthIncomplete:
      "Google connection could not be completed. Please connect again.",
    errorSaveFailed:
      "Google connection could not be saved. Please try again.",
    errorOAuthFailed: "Google connection failed. Please connect again.",
    errorBackend:
      "Google Business connection failed. Check your backend routes.",
  },
  connectPlatform: {
    title: "Connect platform",
    subtitle:
      "Choose a reputation channel to start syncing your brand data.",
    connect: "Connect platform",
    close: "Close",
  },
  account: {
    trial: "Free Trial",
    basic: "Basic",
    premium: "Premium",
    expired: "Expired",
    // Legacy keys kept for safety
    paid: "Premium",
    unpaid: "Free Trial",
  },
  plans: {
    trialBanner:
      "Your free trial ends on {{date}} at {{time}}. Choose a plan to keep full access.",
    trialEndsToday:
      "Your free trial ends today at {{time}}. Choose a plan to keep full access.",
    expiredBanner: "Your free trial has ended. Choose a plan to continue using ReviewLytics.",
    choosePlan: "Choose plan",
  },
  support: {
    title: "Support",
    subtitle: "Get help from our reputation management team.",
    submitRequest: "Submit a request",
    subject: "Subject",
    message: "Message",
    subjectPlaceholder: "Brief summary",
    messagePlaceholder: "Describe your issue…",
    send: "Send",
    sent: "Request submitted",
    faqTitle: "Frequently asked questions",
    faq1q: "How do deletion cases work?",
    faq1a:
      "Select reviews on the Reviews page, submit a case with violation type and justification. Our team reviews and initiates legal removal where applicable.",
    faq2q: "When will my reply appear on Google?",
    faq2a:
      "Replies are queued and sent to Google Business. It may take a few minutes to appear publicly after sending.",
    faq3q: "Can I upgrade my plan mid-cycle?",
    faq3a:
      "Yes. Visit Plans & Billing to upgrade to Professional and unlock included monthly deletions.",
    yourTickets: "Your requests",
    noTickets: "No support requests yet.",
    statusOpen: "Open",
    prioritySupport: "Priority support",
    prioritySupportHint:
      "Professional plan subscribers receive priority response within 24 hours.",
    submitTicket: "Submit ticket",
    ticketSubmitted: "Ticket submitted",
    statusAnswered: "Answered",
    statusClosed: "Closed",
    you: "You",
    supportTeam: "Support team",
    replyPlaceholder: "Write a reply…",
    replySend: "Send reply",
    loadError: "Could not load your requests. Please try again.",
    submitError: "Could not submit your request. Please try again.",
  },
  adminSupport: {
    navTitle: "Support Tickets",
    title: "Support tickets",
    subtitle: "Requests submitted by clients — reply and manage their status.",
    eyebrow: "Client support",
    filterAll: "All",
    searchPlaceholder: "Search subject, name, or email…",
    submittedBy: "Submitted by",
    replyPlaceholder: "Write a reply to the client…",
    replySend: "Send reply",
    close: "Close ticket",
    reopen: "Reopen",
    noTickets: "No tickets found.",
    loadError: "Could not load tickets. Please try again.",
    openCount: "{{count}} open",
  },
  onboarding: {
    stepOf: "Step {{current}} of {{total}}",
    welcomeTitle: "Welcome to ReviewLytics",
    welcomeDesc:
      "Set up your workspace in a few steps. Connect your profile, sync reviews, and start managing your reputation.",
    connectTitle: "Connect Google Business",
    connectDesc:
      "Link your Google Business profile so we can import reviews, ratings, and reply on your behalf.",
    syncTitle: "Sync your reviews",
    syncDesc:
      "Once connected, reviews sync automatically. You can also trigger a manual sync from the Reviews page.",
    exploreTitle: "You're ready to go",
    exploreDesc:
      "Use the Dashboard, Review Hub, and Critical reviews to respond and protect your reputation.",
    skip: "Skip",
    next: "Next",
    finish: "Go to Dashboard",
    connectNow: "Connect now",
    connected: "Connected",
    locationsConnected: "{{count}} location(s) connected",
    notConnectedYet: "Not connected yet",
    goToPlatforms: "Go to Platforms",
    syncConnected:
      "Your profile is connected. Reviews will appear in the Inbox and Reviews pages after sync.",
    syncNotConnected: "Connect a platform first, then return here to continue.",
    skipForNow: "Skip for now",
    continue: "Continue",
    back: "Back",
  },
  documentation: {
    title: "Documentation",
    subtitle: "Guides and references for your reputation workspace.",
  },
  impersonation: {
    actingAs:
      "You are acting as {{target}} ({{email}}). Original admin session: {{admin}}.",
    ending: "Ending…",
    backToAdmin: "Back to Admin",
  },
  platformBenefits: {
    eyebrow: "What you get with ReviewLytics",
    dashboardTitle: "Your reputation advantages",
    dashboardSubtitle:
      "These capabilities work in the background — so you see risk earlier and grow trust faster.",
    dismiss: "Dismiss",
    customAlertsTitle: "Custom Alerts",
    customAlertsBody:
      "Instant notifications when new negative reviews appear — so you can respond before damage spreads.",
    sentimentTitle: "Sentiment Analysis",
    sentimentBody:
      "Automatic analysis of tone and the most common points of criticism across your reviews.",
    localSeoTitle: "Local SEO boost",
    localSeoBody:
      "Improve local SEO with continuously new positive reviews and active replies to customer feedback.",
  },
  admin: {
    usersTitle: "Users",
    usersSubtitle: "Manage accounts and roles.",
    role: "Role",
    account: "Account",
    searchPlaceholder: "Name, email, company…",
    filterRole: "Filter by role",
    filterAccount: "Filter by account status",
    loadingUsers: "Loading users…",
    totalUsers: "{{count}} users total",
    pageOf: "Page {{page}} of {{total}}",
    administrator: "Administrator",
    userRole: "User",
    roleUser: "User (client)",
    roleAdmin: "Administrator",
    openClientProfile: "Open client profile",
    loginAsAdmin: "Log in as admin",
    loggingIn: "Signing in…",
  },
  placeholder: {
    comingSoon: "This page will be available soon.",
    title: "{{title}}",
  },
};

export default en;
